import { useEffect, useState } from 'react';
import paymentLog from '../../services/helpers/PaymentLogger';

// iframe-v1 is the documented bundle for BOTH tokenization (Spreedly.init /
// tokenizeCreditCard) and 3DS2 Global (Spreedly.ThreeDS.Lifecycle/serialize).
const SCRIPT_URL = 'https://core.spreedly.com/iframe/iframe-v1.min.js';

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.Spreedly) {
      resolve();

      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the dead tag so a retry appends a fresh one instead of a second copy beside it.
      script.remove();
      reject(new Error('Failed to load Spreedly script'));
    };

    document.head.appendChild(script);
  }).catch((error: Error) => {
    // A rejected promise must not stay cached: every later mount would inherit the failure
    // for the rest of the session, even once the network is back. Logged once per attempt —
    // every hook awaiting this attempt shares the one rejection.
    scriptPromise = null;
    paymentLog.error('spreedly', 'sdk_load_failed', { payment_method: 'card', message: error?.message });

    throw error;
  });

  return scriptPromise;
}

interface Options {
  /**
   * Off skips the fetch entirely and reports not-loaded. For callers that ask about Spreedly
   * readiness on every checkout (PaymentCoordinator) but must not pull a third-party script into a
   * Stripe- or Adyen-only company's page.
   */
  enabled?: boolean;
}

export default function useSpreedlyScript(options: Options = {}): { loaded: boolean; failed: boolean } {
  const { enabled = true } = options;

  const [loaded, setLoaded] = useState(() => enabled && !!window.Spreedly);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || loaded) return;

    let cancelled = false;

    loadScript()
      .then(() => {
        if (!cancelled) setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // `enabled` is a dep because the company config that decides it can arrive after mount —
    // without it a Spreedly company that resolved late would never load the script. `loadScript`
    // caches, so a re-run after a successful load is a no-op.
  }, [enabled]);

  return { loaded, failed };
}
